import {useCallback, useState} from 'react';
import {NextPage} from 'next';
import {useRouter} from 'next/router';

import {Screen, Text} from '#component/base';
import {SignInForm} from '#component/form';
import {SubmitHandler as SignInFormSubmitHandler} from '#component/form/SignInForm/SignInForm.types';
import {AuthCTAMessages} from '#component/partial';
import {EventType, useAnalytics, UserActionEvent} from '#component/provider/AnalyticsProvider';
import {useAuth, useOnceEffect} from '#hook';
import {useReturnUrl} from '#hook';
import {handleRedirectToUrl} from '#hook/useReturnUrl';
import * as DESTINATION from '#hook/useReturnUrl/defaultDestination';
import {sprinkles} from '#style/sprinkles';

import {parseQueryParams} from './SignIn.util';

export const SignIn: NextPage = () => {
  const [renderContent, setRenderContent] = useState<boolean>(false);
  const {track} = useAnalytics();
  const {signIn, isUserSignedIn} = useAuth();
  const router = useRouter();
  const {targetResolver} = useReturnUrl();
  const {returnUrl, hasAccount, email} = parseQueryParams(router.query);

  const handleSignIn: SignInFormSubmitHandler = async ({email, password, staySignedIn}) => {
    await signIn(
      {email, password},
      {
        rememberAuth: staySignedIn,
        redirect: targetResolver(returnUrl as string),
      },
    );
    track<UserActionEvent>({evtName: EventType.UserAction, actionName: 'Auth Success'});
  };

  const onPageLoad = useCallback(async () => {
    const isSignedIn = await isUserSignedIn({async: true});
    const resolveReturnUrl = targetResolver(returnUrl as string);

    if (isSignedIn) {
      await handleRedirectToUrl(router, resolveReturnUrl, DESTINATION.HOME);
    } else {
      setRenderContent(true);
    }
  }, [router, isUserSignedIn, returnUrl, targetResolver]);

  useOnceEffect(() => {
    onPageLoad();
  });

  return (
    <Screen
      contentContainerClassName={sprinkles({
        flex: 'flex',
        content: ['end', 'end', 'center'],
        dir: 'col',
      })}
      // Because this page also handles redirection to return URL, we should
      // not set the `userState` prop to disable user state checks. This way
      // we can avoid potential conflicts and complications.
      pageName="Log In"
      renderContent={renderContent}
      sidePanelImageUrl="/image/login.jpg">
      <div
        className={sprinkles({
          direction: 'col',
          flex: 'flex',
          height: 'full',
          placeContent: ['end', 'center'],
        })}>
        <AuthCTAMessages
          title={
            <>
              Welcome
              <br />
              back
            </>
          }
        />
        {hasAccount && (
          <Text color="crimson" className={sprinkles({pB: 20})}>
            We noticed that you already have an account. To update your subscription, please log in
            below.
          </Text>
        )}
        <SignInForm email={email} returnUrl={returnUrl as string} onSubmit={handleSignIn} />
      </div>
    </Screen>
  );
};
